import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  Loader2,
  PackageCheck,
  Pencil,
  Printer,
  Ruler,
  Truck,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody } from "@/components/ui/Card";
import { HoverHintContent } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  TRANSFER_INVENTORY_SHORTFALL_CHIP_LABEL,
  TRANSFER_INVENTORY_SHORTFALL_HOVER,
  TRANSFER_PENDING_QUANTITY_CHANGE_HOVER,
  transferPendingQuantityChangeChipLabel,
} from "@/pages/manufacturing/transfersWorkbenchTypes";

import type { SkuQuantity, ShippingUpsView, OutboundQueueEntry } from "./shippingHubTypes";

function formatShipByDate(raw: string | null): string {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return raw;
  }
}

function allItemsBoxed(skuQuantities: SkuQuantity[] | null): boolean {
  if (!skuQuantities || skuQuantities.length === 0) return true;
  return skuQuantities.every((item) => item.boxed >= item.quantity);
}

function totalQuantity(skuQuantities: SkuQuantity[] | null): number {
  return (skuQuantities ?? []).reduce((sum, item) => sum + (item.quantity ?? 0), 0);
}

function totalBoxed(skuQuantities: SkuQuantity[] | null): number {
  return (skuQuantities ?? []).reduce((sum, item) => sum + (item.boxed ?? 0), 0);
}

function cartonKey(item: SkuQuantity): number {
  return item.carton_key ?? 0;
}

/** Distinct carton keys, sorted ascending (same ordering as packing slip `DENSE_RANK` over `carton_key`). */
function orderedCartonKeysForItems(items: SkuQuantity[]): number[] {
  return [...new Set(items.map(cartonKey))].sort((a, b) => a - b);
}

/** Rows in packing-slip carton order with dense-rank carton labels (formerly one table per carton). */
function shipmentDetailRowsInCartonOrder(items: SkuQuantity[]): Array<{ cartonLabel: number; item: SkuQuantity }> {
  const ordered = orderedCartonKeysForItems(items);
  const out: Array<{ cartonLabel: number; item: SkuQuantity }> = [];
  for (let cartonIndex0 = 0; cartonIndex0 < ordered.length; cartonIndex0 += 1) {
    const key = ordered[cartonIndex0];
    if (key === undefined) continue;
    const cartonLabel = cartonIndex0 + 1;
    const cartonItems = items
      .filter((line) => cartonKey(line) === key)
      .slice()
      .sort((lineA, lineB) => String(lineA.sku).localeCompare(String(lineB.sku)));
    for (const item of cartonItems) {
      out.push({ cartonLabel, item });
    }
  }
  return out;
}

export interface OutboundQueueCardProps {
  entry: OutboundQueueEntry;
  onToggleReady: () => void;
  onMarkShipped: () => void;
  onOpenPackingSlip: () => void;
  onOpenPackageDetails?: () => void;
  onPrintFreightDocs?: () => void;
  onDownloadFreightDocs?: () => void;
  packageDetailsComplete?: boolean;
  freightDocsPrintPending?: boolean;
  freightDocsDownloadPending?: boolean;
  showFreightDocs?: boolean;
  showUps?: boolean;
  onOpenUps: (view: ShippingUpsView) => void;
  onReprintCartonLabel?: () => void;
  showReprintCartonLabel?: boolean;
  isTogglingReady: boolean;
  isMarkingShipped: boolean;
}

export function OutboundQueueCard({
  entry,
  onToggleReady,
  onMarkShipped,
  onOpenPackingSlip,
  onOpenPackageDetails,
  onPrintFreightDocs,
  onDownloadFreightDocs,
  packageDetailsComplete = false,
  freightDocsPrintPending = false,
  freightDocsDownloadPending = false,
  showFreightDocs = false,
  showUps = true,
  onOpenUps,
  onReprintCartonLabel,
  showReprintCartonLabel = false,
  isTogglingReady,
  isMarkingShipped,
}: OutboundQueueCardProps) {
  const [expanded, setExpanded] = useState(false);

  if (entry.kind === "shipment") {
    const { shipment } = entry;
    const isReady = Boolean(shipment.ready_for_pickup);
    const hasTracking = Boolean(shipment.tracking);
    const items = shipment.sku_quantities ?? [];
    const fullyBoxed = allItemsBoxed(items);
    const cartons = Math.max(0, Number(shipment.carton_count ?? 0));
    const detailRows = shipmentDetailRowsInCartonOrder(items);

    return (
      <div
        className={cn(
          "rounded-lg border border-slate-200 bg-white shadow-sm",
          !isReady && !fullyBoxed && "border-amber-200/80",
        )}
      >
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          className={cn(
            "flex w-full flex-wrap items-start justify-between gap-3 px-4 py-3 text-left",
            "hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-0",
          )}
        >
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold text-slate-900">
              Shipment {shipment.shipment_id}
              {shipment.manufacturer_sales_order_number?.trim() ? (
                <span className="font-normal text-slate-600">
                  {" "}
                  · {shipment.manufacturer_sales_order_number.trim()}
                </span>
              ) : null}
            </span>
            {isReady ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                Ready for pickup
              </span>
            ) : null}
            <span className="text-sm text-slate-500">
              Ship by{" "}
              <span className="font-medium text-slate-700">{formatShipByDate(shipment.mfr_ship_by_date)}</span>
            </span>
            <span className="text-sm tabular-nums text-slate-600">
              {cartons} {cartons === 1 ? "carton" : "cartons"}
            </span>
            {hasTracking ? (
              <span className="flex items-center gap-1 text-sm text-slate-600">
                <Truck className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                {shipment.carrier} · {shipment.tracking}
              </span>
            ) : null}
          </div>
          <span className="ml-auto flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs text-slate-500">
            {expanded ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                Collapse
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                Details
              </>
            )}
          </span>
        </button>

        {expanded ? (
          <div className="border-t border-slate-100 px-4 pb-4 pt-3">
            {items.length > 0 ? (
              <div className="mb-4 max-h-[min(420px,60vh)] overflow-auto rounded-md border border-slate-100">
                <table className="w-full min-w-[420px] border-collapse text-left text-sm">
                  <thead className="sticky top-0 z-[1] border-b border-slate-200 bg-slate-50/95 backdrop-blur text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="whitespace-nowrap py-2 pl-3 pr-3 tabular-nums">Carton</th>
                      <th className="py-2 pr-4">SKU</th>
                      <th className="py-2 pr-4 text-right tabular-nums">Qty</th>
                      <th className="py-2 pr-4 text-right tabular-nums">Boxed</th>
                      <th className="py-2 pr-3">Remark</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {detailRows.map(({ cartonLabel, item }, index) => (
                      <tr key={`${String(cartonLabel)}-${item.sku}-${String(index)}`} className="text-slate-700">
                        <td className="whitespace-nowrap py-1.5 pl-3 pr-3 text-center tabular-nums text-slate-600">
                          {cartonLabel}
                        </td>
                        <td className="py-1.5 pr-4 font-mono text-xs">{item.sku}</td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">{item.quantity}</td>
                        <td
                          className={cn(
                            "py-1.5 pr-4 text-right tabular-nums",
                            item.boxed >= item.quantity ? "text-emerald-700" : "text-amber-700",
                          )}
                        >
                          {item.boxed}
                        </td>
                        <td className="py-1.5 pr-3 text-xs text-slate-500">{item.remark ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mb-4 text-sm text-slate-500">No items loaded for this shipment.</p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={isReady ? "secondary" : "primary"}
                disabled={isTogglingReady || (!isReady && !fullyBoxed)}
                onClick={onToggleReady}
              >
                {isTogglingReady ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : isReady ? (
                  <PackageCheck className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                ) : (
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                )}
                {isReady ? "Unmark Ready" : "Mark Ready for Pickup"}
              </Button>
              {!isReady && !fullyBoxed ? (
                <span className="self-center text-xs text-slate-400">Box all items before marking ready.</span>
              ) : null}

              <Button
                size="sm"
                variant="primary"
                disabled={isMarkingShipped || !isReady}
                onClick={onMarkShipped}
                className={cn(!isReady && "invisible")}
              >
                {isMarkingShipped ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Truck className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                )}
                {hasTracking ? "Mark as Picked Up" : "Mark Shipped"}
              </Button>
            </div>

            {!isReady && !fullyBoxed ? (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {totalQuantity(items) - totalBoxed(items)} item(s) still need box labels before this shipment can be
                marked ready.
              </p>
            ) : null}

            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
              {showFreightDocs ? (
                <>
                  <Button size="sm" variant="secondary" onClick={() => onOpenPackageDetails?.()}>
                    {packageDetailsComplete ? (
                      <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <Ruler className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    )}
                    {packageDetailsComplete ? "Edit carton dims and weight" : "Enter carton dims and weight"}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!packageDetailsComplete || freightDocsPrintPending || freightDocsDownloadPending}
                    title={
                      packageDetailsComplete
                        ? undefined
                        : "Enter carton dims and weight before printing."
                    }
                    onClick={() => onPrintFreightDocs?.()}
                  >
                    {freightDocsPrintPending ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                      <Printer className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    )}
                    Print packing slip and commercial invoice
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!packageDetailsComplete || freightDocsPrintPending || freightDocsDownloadPending}
                    title={
                      packageDetailsComplete
                        ? undefined
                        : "Enter carton dims and weight before downloading."
                    }
                    onClick={() => onDownloadFreightDocs?.()}
                  >
                    {freightDocsDownloadPending ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                      <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    )}
                    Download packing slip and commercial invoice
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="secondary" onClick={onOpenPackingSlip}>
                  <FileText className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  Packing Slip
                </Button>
              )}

              {showUps ? (
                hasTracking ? (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => onOpenUps("result")}>
                      <Printer className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      Reprint Labels
                    </Button>
                    {!shipment.date_shipped ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onOpenUps("void")}
                        className="text-slate-500 hover:text-rose-700"
                      >
                        <XCircle className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                        Void UPS Shipment
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <Button size="sm" variant="secondary" onClick={() => onOpenUps("create")}>
                    <Truck className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Create UPS Shipment
                  </Button>
                )
              ) : null}

              {showReprintCartonLabel && onReprintCartonLabel != null ? (
                <Button size="sm" variant="secondary" onClick={onReprintCartonLabel}>
                  <Printer className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  Reprint Carton Label
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  const { transfer } = entry;
  const isReady = Boolean(transfer.ready_for_pickup);
  const hasTracking = Boolean(transfer.tracking);
  const cartons = Math.max(0, Number(transfer.box_count ?? 0));
  const canShip = transfer.status === "ready_to_ship";
  const pendingQtyChanges = Number(transfer.pending_pack_qty_request_count) || 0;
  const inventoryShortfall = Boolean(transfer.has_inventory_shortfall);

  return (
    <Card className={cn(transfer.is_overdue && "border-rose-300", !isReady && "border-amber-200/80")}>
      <CardBody className="space-y-0 p-0">
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          className={cn(
            "flex w-full flex-wrap items-start justify-between gap-3 px-4 py-3 text-left",
            "rounded-md hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-0",
          )}
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-semibold text-slate-900">{transfer.transfer_number}</span>
              {transfer.destination_name ? (
                <span className="text-sm text-slate-500">→ {transfer.destination_name}</span>
              ) : null}
              {canShip ? (
                isReady ? (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    Ready for pickup
                  </span>
                ) : (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-800 ring-1 ring-sky-200">
                    Ready to ship
                  </span>
                )
              ) : null}
              {pendingQtyChanges > 0 ? (
                <HoverHintContent hoverHint={TRANSFER_PENDING_QUANTITY_CHANGE_HOVER}>
                  <Badge tone="danger">{transferPendingQuantityChangeChipLabel(pendingQtyChanges)}</Badge>
                </HoverHintContent>
              ) : null}
              {inventoryShortfall ? (
                <HoverHintContent hoverHint={TRANSFER_INVENTORY_SHORTFALL_HOVER}>
                  <Badge tone="danger">{TRANSFER_INVENTORY_SHORTFALL_CHIP_LABEL}</Badge>
                </HoverHintContent>
              ) : null}
              {transfer.ship_by_date ? (
                <span className="text-sm text-slate-500">
                  Ship by{" "}
                  <span className="font-medium text-slate-700">{formatShipByDate(transfer.ship_by_date)}</span>
                </span>
              ) : null}
              <span className="text-sm tabular-nums text-slate-600">
                {cartons} {cartons === 1 ? "carton" : "cartons"}
              </span>
              {hasTracking ? (
                <span className="flex items-center gap-1 text-sm text-slate-600">
                  <Truck className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                  {transfer.carrier} · {transfer.tracking}
                </span>
              ) : null}
              {transfer.is_overdue ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800">
                  <AlertTriangle className="h-3 w-3" aria-hidden />
                  Overdue
                </span>
              ) : null}
              {transfer.mv_transfer_document_number ? (
                <span className="text-xs text-slate-400">MV #{transfer.mv_transfer_document_number}</span>
              ) : null}
            </div>
            {transfer.notes ? <p className="mt-1 text-xs text-slate-500 italic">{transfer.notes}</p> : null}
          </div>
          <span className="flex shrink-0 items-center gap-1 self-start rounded px-2 py-1 text-xs text-slate-500">
            {expanded ? <ChevronUp className="h-3.5 w-3.5" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
            {expanded ? "Collapse" : "Details"}
          </span>
        </button>

        {expanded ? (
          <div className="border-t border-slate-100 px-4 pb-4 pt-3">
            {transfer.items.length > 0 ? (
              <div className="mb-4 overflow-x-auto">
                <table className="w-full min-w-[320px] text-xs">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="pb-1 pr-4 font-medium">SKU</th>
                      <th className="pb-1 pr-4 font-medium">Description</th>
                      <th className="pb-1 text-right font-medium">Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {transfer.items.map((item, index) => (
                      <tr key={index} className="text-slate-700">
                        <td className="py-1 pr-4 font-mono">{item.sku_id}</td>
                        <td className="py-1 pr-4">{item.description ?? "—"}</td>
                        <td className="py-1 text-right">{Number(item.quantity).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mb-4 text-sm text-slate-500">No lines loaded for this transfer.</p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={isReady ? "secondary" : "primary"}
                disabled={isTogglingReady || !canShip || pendingQtyChanges > 0}
                onClick={onToggleReady}
              >
                {isTogglingReady ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : isReady ? (
                  <PackageCheck className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                ) : (
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                )}
                {isReady ? "Unmark Ready" : "Mark Ready for Pickup"}
              </Button>

              <Button
                size="sm"
                variant="primary"
                disabled={isMarkingShipped || !isReady || !canShip || pendingQtyChanges > 0}
                onClick={onMarkShipped}
                className={cn((!isReady || !canShip || pendingQtyChanges > 0) && "invisible")}
              >
                {isMarkingShipped ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Truck className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                )}
                {hasTracking ? "Mark as Sent" : "Mark Sent"}
              </Button>
            </div>

            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
              <Button size="sm" variant="secondary" onClick={onOpenPackingSlip}>
                <FileText className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Packing Slip
              </Button>

              {hasTracking ? (
                <>
                  <Button size="sm" variant="secondary" onClick={() => onOpenUps("result")}>
                    <Printer className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Reprint Labels
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onOpenUps("void")}
                    className="text-slate-500 hover:text-rose-700"
                  >
                    <XCircle className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Void UPS Shipment
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!canShip || pendingQtyChanges > 0}
                  onClick={() => onOpenUps("create")}
                >
                  <Truck className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  Create UPS Shipment
                </Button>
              )}

              {showReprintCartonLabel && onReprintCartonLabel != null ? (
                <Button size="sm" variant="secondary" onClick={onReprintCartonLabel}>
                  <Printer className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  Reprint Carton Label
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
